import { z } from "zod";
import {
  actionToolName,
  uniqueActionToolName,
} from "./agentWorkflowActionBindings";

/**
 * Agent config key listing the Workbooks attached to an Agent definition. Each
 * becomes one tool that runs that Workbook and returns its Results, the way
 * `workflowActions` exposes workflows.
 */
export const WORKBOOK_ACTIONS_CONFIG_KEY = "workbookActions";

export const WORKBOOK_ACTION_BINDINGS_MAX = 20;

export const workbookActionBindingSchema = z.object({ workbookId: z.uuid() });

export type WorkbookActionBinding = z.infer<typeof workbookActionBindingSchema>;

/** Reads `config.workbookActions`, dropping invalid rows and repeated Workbooks. */
export function parseWorkbookActionBindings(
  config: unknown,
): WorkbookActionBinding[] {
  if (!config || typeof config !== "object") return [];
  const raw = (config as Record<string, unknown>)[WORKBOOK_ACTIONS_CONFIG_KEY];
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const bindings: WorkbookActionBinding[] = [];
  for (const item of raw) {
    const parsed = workbookActionBindingSchema.safeParse(item);
    if (!parsed.success || seen.has(parsed.data.workbookId)) continue;
    seen.add(parsed.data.workbookId);
    bindings.push(parsed.data);
    if (bindings.length >= WORKBOOK_ACTION_BINDINGS_MAX) break;
  }
  return bindings;
}

export function withWorkbookActionBinding(
  config: Record<string, unknown>,
  workbookId: string,
): Record<string, unknown> {
  const current = parseWorkbookActionBindings(config);
  if (
    current.some((binding) => binding.workbookId === workbookId) ||
    current.length >= WORKBOOK_ACTION_BINDINGS_MAX
  )
    return config;
  return {
    ...config,
    [WORKBOOK_ACTIONS_CONFIG_KEY]: [...current, { workbookId }],
  };
}

export function withoutWorkbookActionBinding(
  config: Record<string, unknown>,
  workbookId: string,
): Record<string, unknown> {
  return {
    ...config,
    [WORKBOOK_ACTIONS_CONFIG_KEY]: parseWorkbookActionBindings(config).filter(
      (binding) => binding.workbookId !== workbookId,
    ),
  };
}

/** The model-facing tool name of an attached Workbook, unique among `used`. */
export function uniqueWorkbookActionToolName(
  workbookName: string,
  used: Set<string>,
): string {
  return uniqueActionToolName(
    actionToolName("workbook_", "workbook_action", workbookName),
    used,
    `workbook "${workbookName}"`,
  );
}
